import {
  BALANCE_TRANSACTION,
  BlotterDensity,
  cancelPendingApprovalWithdrawalRequest,
  CustomerBalanceTransactionStatusEnum,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  usePersistedBlotterTable,
  useSocketClient,
  useWsBlotterTable,
  type Column,
  type CustomerBalanceTransaction,
  type DateRangeFilter,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ValueGetterParams } from 'ag-grid-community';
import { compact, pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { useTransfersFilter } from '../../../../components/Filters/FilterBuilder';

const messages = defineMessages({
  notSpecified: {
    defaultMessage: 'Not Specified',
    id: 'Blotters.TransfersBlotter.notSpecified',
  },
});

// TODO these columns are wrong, the fields that is. Need to type correctly so we get alerted
const COLUMNS: Column[] = [
  { type: 'date', field: 'Timestamp', initialWidth: 225, sortable: true, initialSort: '-' },
  { type: 'account', field: 'MarketAccount' },
  { type: 'currency', field: 'Currency', initialWidth: 200 },
  { type: 'size', field: 'Quantity', params: { currencyField: 'Currency' }, initialWidth: 200 },
  { type: 'transactionType', field: 'TransactionType', initialWidth: 150 },
  { type: 'transferStatus', field: 'Status', initialWidth: 225 },
  { type: 'text', field: 'ExternalComments', title: { intlKey: 'comments' } },
  {
    type: 'custom',
    field: 'TxHash',
    initialWidth: 500,
    params: {
      valueGetter: ({ data, context }: ValueGetterParams) =>
        data.Status === CustomerBalanceTransactionStatusEnum.Completed
          ? data.TxHash || context.current.intl.formatMessage(messages.notSpecified)
          : '',
    },
  },
];

interface UseTransfersBlotterProps {
  maxRows: number;
  tag: string;
}

const DEFAULT_SORT = '-Timestamp';

export const useTransfersBlotter = ({ maxRows, tag }: UseTransfersBlotterProps) => {
  const client = useSocketClient();

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<CustomerBalanceTransaction>) =>
      compact([cancelPendingApprovalWithdrawalRequest(params, client)]),
    [client]
  );

  const persistedBlotterTable = usePersistedBlotterTable('transfers');

  const filteredTrades = useTransfersFilter({ persistedBlotterTable });
  const { blotterTableFilterProps, filterBuilderProps } = filteredTrades;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<WebsocketRequest>(
    () => ({
      name: BALANCE_TRANSACTION,
      tag: `${tag}/useTransfersBlotter`,
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredTrades.filter)),
    }),
    [tag, filteredTrades.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, CustomerBalanceTransaction>({
    request,
    columns: COLUMNS,
    persistence: persistedBlotterTable,
    rowID: 'TransactionID',

    density: BlotterDensity.Comfortable,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      animateRows: true,
      rowHeight: 40,
    },
  });

  return useMemo(
    () => ({ blotterTable, blotterTableFilterProps, filterBuilderAccordion }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};

function onlyServerFilterKeys(filter: DateRangeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate']);
}
